/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Megaphone, Plus, Trash2, Power, Pencil } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";

type AnnouncementType = "INFO" | "WARNING" | "SUCCESS" | "GIVEAWAY";

interface Announcement {
  id: number;
  title: string;
  body: string;
  type: AnnouncementType;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
}

const TYPE_STYLES: Record<AnnouncementType, { badge: string; icon: string }> = {
  INFO:     { badge: "bg-blue-100 text-blue-700",    icon: "ℹ️" },
  WARNING:  { badge: "bg-yellow-100 text-yellow-700", icon: "⚠️" },
  SUCCESS:  { badge: "bg-green-100 text-green-700",   icon: "✅" },
  GIVEAWAY: { badge: "bg-purple-100 text-purple-700", icon: "🎁" },
};

const emptyForm = { title: "", body: "", type: "INFO" as AnnouncementType, expiresAt: "" };

export default function AnnouncementsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{ title?: string; body?: string }>({});

  const { data, isLoading } = useQuery<Announcement[]>({
    queryKey: ["announcements"],
    queryFn: () => apiClient.get(API_ENDPOINTS.ANNOUNCEMENTS.LIST),
  });
  const announcements = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["announcements"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: typeof form) => apiClient.post(API_ENDPOINTS.ANNOUNCEMENTS.CREATE, d),
    onSuccess: () => { toast.success("Announcement created"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: typeof form }) =>
      apiClient.patch(API_ENDPOINTS.ANNOUNCEMENTS.UPDATE(id), d),
    onSuccess: () => { toast.success("Announcement updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: (id: number) => apiClient.post(API_ENDPOINTS.ANNOUNCEMENTS.TOGGLE(id), {}),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.ANNOUNCEMENTS.DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a: Announcement) => {
    setEditing(a);
    setForm({
      title: a.title,
      body: a.body,
      type: a.type,
      expiresAt: a.expiresAt ? a.expiresAt.slice(0, 10) : "",
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.title.trim()) newErrors.title = "Title is required";
    if (!form.body.trim()) newErrors.body = "Message is required";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    const payload = { ...form, expiresAt: form.expiresAt || null };
    if (editing) update({ id: editing.id, d: payload as any });
    else create(payload as any);
  };

  const handleDelete = (a: Announcement) => {
    if (!confirm(`Delete "${a.title}"?`)) return;
    remove(a.id);
  };

  const active = announcements.filter((a) => a.isActive);
  const inactive = announcements.filter((a) => !a.isActive);

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="h-8 w-8 text-purple-500" />
            Announcements
          </h1>
          <p className="text-gray-500 mt-1">
            In-app banners shown to all users in the mobile app
          </p>
        </div>
        <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {(["INFO","WARNING","SUCCESS","GIVEAWAY"] as AnnouncementType[]).map((t) => {
          const count = announcements.filter((a) => a.type === t && a.isActive).length;
          const s = TYPE_STYLES[t];
          return (
            <div key={t} className="bg-white rounded-xl border shadow-sm p-4 flex items-center gap-3">
              <span className="text-2xl">{s.icon}</span>
              <div>
                <p className="text-xl font-bold text-gray-900">{count}</p>
                <p className="text-xs text-gray-500">{t.charAt(0) + t.slice(1).toLowerCase()} active</p>
              </div>
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No announcements yet</p>
          <p className="text-sm">Create one to show it to all app users</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active */}
          {active.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">
                Active ({active.length})
              </p>
              <div className="space-y-3">
                {active.map((a) => <AnnouncementRow key={a.id} a={a} onEdit={openEdit} onToggle={toggle} onDelete={handleDelete} />)}
              </div>
            </div>
          )}

          {/* Inactive */}
          {inactive.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">
                Inactive ({inactive.length})
              </p>
              <div className="space-y-3 opacity-60">
                {inactive.map((a) => <AnnouncementRow key={a.id} a={a} onEdit={openEdit} onToggle={toggle} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Announcement" : "New Announcement"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => { setForm((f) => ({ ...f, title: e.target.value })); if (e.target.value.trim()) setErrors((er) => ({ ...er, title: undefined })); }}
                placeholder="e.g. 🎁 Giveaway Winner Announced!"
                className={errors.title ? "border-red-400" : ""}
              />
              {errors.title && <p className="text-xs text-red-500 mt-1 font-medium">{errors.title}</p>}
            </div>
            <div>
              <Label className="mb-1 block text-sm">Message <span className="text-red-500">*</span></Label>
              <textarea
                value={form.body}
                onChange={(e) => { setForm((f) => ({ ...f, body: e.target.value })); if (e.target.value.trim()) setErrors((er) => ({ ...er, body: undefined })); }}
                placeholder="Shown to users in the app…"
                rows={3}
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${errors.body ? "border-red-400" : "border-gray-300"}`}
              />
              {errors.body && <p className="text-xs text-red-500 mt-1 font-medium">{errors.body}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as AnnouncementType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INFO">ℹ️ Info</SelectItem>
                    <SelectItem value="WARNING">⚠️ Warning</SelectItem>
                    <SelectItem value="SUCCESS">✅ Success</SelectItem>
                    <SelectItem value="GIVEAWAY">🎁 Giveaway</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Expires on (optional)</Label>
                <Input type="date" value={form.expiresAt} onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function AnnouncementRow({
  a, onEdit, onToggle, onDelete,
}: {
  a: Announcement;
  onEdit: (a: Announcement) => void;
  onToggle: (id: number) => void;
  onDelete: (a: Announcement) => void;
}) {
  const s = TYPE_STYLES[a.type];
  const isExpired = a.expiresAt ? new Date(a.expiresAt) < new Date() : false;

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 flex items-start gap-4">
      <span className="text-2xl shrink-0 mt-0.5">{s.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-semibold text-gray-900">{a.title}</p>
          <Badge className={`text-xs ${s.badge}`}>{a.type}</Badge>
          {isExpired && <Badge className="text-xs bg-red-100 text-red-600">Expired</Badge>}
        </div>
        <p className="text-sm text-gray-600 mb-2">{a.body}</p>
        <div className="flex gap-3 text-xs text-gray-400">
          <span>Created {new Date(a.createdAt).toLocaleDateString()}</span>
          {a.expiresAt && <span>· Expires {new Date(a.expiresAt).toLocaleDateString()}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => onEdit(a)} title="Edit">
          <Pencil className="h-4 w-4 text-gray-400" />
        </Button>
        <Button
          variant="ghost" size="sm"
          onClick={() => onToggle(a.id)}
          title={a.isActive ? "Deactivate" : "Activate"}
          className={a.isActive ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}
        >
          <Power className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(a)} title="Delete">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
